/**
 * <report-issue-component> - "Report an issue with this product" (blocks/report-issue.liquid).
 *
 * Opens the form in a pop-up and sends it with Shopify's contact form, without leaving the product page.
 * If Shopify asks the shopper to solve a captcha first, the shopper is taken to that page and the message is
 * sent after it is solved (nothing is lost).
 */
class ReportIssueComponent extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-report-dialog]');
    this.form = this.querySelector('[data-report-form]');
    this.message = this.querySelector('[data-report-message]');
    this.fields = this.querySelector('[data-report-fields]');

    this.querySelector('[data-report-open]')?.addEventListener('click', this.#open);
    this.querySelector('[data-report-close]')?.addEventListener('click', this.#close);
    this.dialog?.addEventListener('click', this.#onBackdrop);
    this.form?.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    this.querySelector('[data-report-open]')?.removeEventListener('click', this.#open);
    this.querySelector('[data-report-close]')?.removeEventListener('click', this.#close);
    this.dialog?.removeEventListener('click', this.#onBackdrop);
    this.form?.removeEventListener('submit', this.#onSubmit);
  }

  #open = () => {
    if (!(this.dialog instanceof HTMLDialogElement)) return;
    // The link of the page the shopper is on (includes the chosen variant).
    const url = this.querySelector('[data-report-url]');
    if (url instanceof HTMLInputElement) {
      const current = new URL(window.location.href);
      current.hash = '';
      url.value = current.toString();
    }
    this.#showMessage('');
    if (this.fields instanceof HTMLElement) this.fields.hidden = false;
    this.dialog.showModal();
  };

  #close = () => {
    if (this.dialog instanceof HTMLDialogElement) this.dialog.close();
  };

  /** @param {MouseEvent} event */
  #onBackdrop = (event) => {
    if (event.target === this.dialog) this.#close(); // a click on the dark area around the form
  };

  /** @param {string} text */
  #showMessage(text) {
    if (!(this.message instanceof HTMLElement)) return;
    this.message.textContent = text;
    this.message.hidden = !text;
  }

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    event.preventDefault();
    const form = this.form;
    if (!(form instanceof HTMLFormElement) || !form.reportValidity()) return;

    const button = form.querySelector('[data-report-submit]');
    if (button instanceof HTMLButtonElement) button.disabled = true;
    this.#showMessage('');

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'text/html' },
        credentials: 'same-origin',
      });

      if (response.url.includes('/challenge')) {
        // Shopify wants a captcha first: continue on its page, the message is sent after it is solved.
        window.location.assign(response.url);
        return;
      }

      // Shopify answers a sent contact form with a redirect (to ...?contact_posted=true); a rejected one is not redirected.
      if (response.ok && (/posted=true/.test(response.url) || response.redirected)) {
        form.reset();
        if (this.fields instanceof HTMLElement) this.fields.hidden = true;
        this.#showMessage(this.dataset.successMessage || 'Thank you! Your report was sent.');
        return;
      }
      throw new Error(`Report failed (${response.status})`);
    } catch (error) {
      console.error(error);
      this.#showMessage(this.dataset.errorMessage || 'Sorry, we could not send your report.');
    } finally {
      if (button instanceof HTMLButtonElement) button.disabled = false;
    }
  };
}

if (!customElements.get('report-issue-component')) {
  customElements.define('report-issue-component', ReportIssueComponent);
}
